import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    Switch,
    FormControlLabel,
    CircularProgress,
} from '@mui/material';
import {
    FaTimes,
    FaSave,
    FaPlus,
} from 'react-icons/fa';
import dayjs from 'dayjs';
import SectionTitle from '../Common/Title';
import { } from '../../services/categoriesService';
import OptionSection from './OptionSection';
import PricingSection from './PricingSection';

const MENU_ITEM_TYPES = [
    { id: 'appetizer', name: 'Apéritifs', description: 'Snacks, tapas, amuse-bouches...' },
    { id: 'starter', name: 'Entrées', description: 'Entrées, soupes, salades composées...' },
    { id: 'main', name: 'Plats', description: 'Plats principaux' },
    { id: 'side', name: 'Accompagnements', description: 'Frites, riz, légumes...' },
    { id: 'salad', name: 'Salades', description: 'Salades en accompagnement' },
    { id: 'sauce', name: 'Sauces', description: 'Sauces et condiments' },
    { id: 'extra', name: 'Suppléments', description: 'Extras et toppings' },
    { id: 'drink', name: 'Boissons froides', description: 'Sodas, jus, bières...' },
    { id: 'hot_drink', name: 'Boissons chaudes', description: 'Cafés, thés, chocolats chauds...' },
    { id: 'dessert', name: 'Desserts', description: 'Desserts, pâtisseries, glaces...' }
];


function BundleEditPanel({ bundle, onSave, onClose, menuItems, categories }) {
    const getInitialFormData = (bundleData) => ({
        id: bundleData?.id || '',
        name: bundleData?.name || '',
        description: bundleData?.description || '',
        prices: bundleData?.prices || [],
        available: bundleData?.available ?? true,
        options: bundleData?.options || [],
        validityPeriod: {
            enabled: bundleData?.validityPeriod?.enabled ?? false,
            start: bundleData?.validityPeriod?.start ?
                dayjs(bundleData.validityPeriod.start, 'HH:mm') :
                dayjs().set('hour', 11).set('minute', 0),
            end: bundleData?.validityPeriod?.end ?
                dayjs(bundleData.validityPeriod.end, 'HH:mm') :
                dayjs().set('hour', 22).set('minute', 0),
            days: bundleData?.validityPeriod?.days || [1, 2, 3, 4, 5, 6, 7],
            restrictions: bundleData?.validityPeriod?.restrictions || {}
        }
    });

    // Initialiser le state avec la fonction
    const [formData, setFormData] = useState(getInitialFormData(bundle));
    const [isSaving, setIsSaving] = useState(false);

    // Ajouter l'effet pour réinitialiser le formulaire quand le bundle change
    useEffect(() => {
        setFormData(getInitialFormData(bundle));
    }, [bundle]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleAddOption = (type) => {
        setFormData(prevData => ({
            ...prevData,
            options: [
                ...prevData.options,
                {
                    id: Date.now().toString(),
                    type: type,
                    items: [],
                    required: true,
                    quantity: 1
                }
            ]
        }));
    };

    const handleRemoveOption = (optionId) => {
        setFormData(prevData => ({
            ...prevData,
            options: prevData.options.filter(opt => opt.id !== optionId)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);

        try {
            // Prépare les données pour la sauvegarde
            const bundleToSave = {
                ...formData,
                id: bundle?.id, // S'assurer que l'ID est conservé
                validityPeriod: {
                    ...formData.validityPeriod,
                    start: formData.validityPeriod.start.format('HH:mm'),
                    end: formData.validityPeriod.end.format('HH:mm'),
                    restrictions: Object.entries(formData.validityPeriod.restrictions || {}).reduce((acc, [day, restriction]) => {
                        if (restriction.enabled) {
                            acc[day] = {
                                ...restriction,
                                start: dayjs(restriction.start).format('HH:mm'),
                                end: dayjs(restriction.end).format('HH:mm')
                            };
                        }
                        return acc;
                    }, {})
                },
                // Nettoie et normalise les options
                options: formData.options.map(option => ({
                    ...option,
                    items: option.items.map(item => ({
                        id: item.id,
                        extraPrice: item.extraPrice || 0
                    }))
                })),
                // Prix et remises
                pricingMode: formData.pricingMode,
                fixedPrice: formData.pricingMode === 'fixed' ? formData.fixedPrice : null,
                discount: formData.pricingMode === 'automatic' ? {
                    type: formData.discount.type,
                    value: formData.discount.value
                } : null,
                updatedAt: new Date().toISOString()
            };

            await onSave(bundleToSave);

        } catch (error) {
            console.error("Error saving bundle:", error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleItemExtraPriceChange = (optionId, itemId, price) => {
        console.log("handleItemExtraPriceChange", {
            optionId: optionId,
            price: price,
            itemId: itemId,
            'formData.options': formData.options,
        });
        setFormData(prev => ({
            ...prev,
            options: prev.options.map(opt => {
                if (opt.id === optionId) {
                    console.log("opt", opt);
                    return {
                        ...opt,
                        items: opt.items.map(item =>
                            item.id === itemId
                                ? { ...item, extraPrice: price }
                                : item
                        )
                    };
                }
                return opt;
            })
        }));
    };

    const handleItemSelection = (optionId, itemId) => {
        setFormData(prev => ({
            ...prev,
            options: prev.options.map(opt => {
                if (opt.id === optionId) {
                    const existingItem = opt.items.find(item => item.id === itemId);
                    return {
                        ...opt,
                        items: existingItem
                            ? opt.items.filter(item => item.id !== itemId)
                            : [...opt.items, { id: itemId, extraPrice: 0 }]
                    };
                }
                return opt;
            })
        }));
    };


    const handleMultipleSelection = (optionId, itemIds, isSelected) => {
        setFormData(prev => ({
            ...prev,
            options: prev.options.map(opt => {
                if (opt.id === optionId) {
                    const newItems = isSelected
                        ? [
                            ...opt.items,
                            ...itemIds,
                        ]
                        : opt.items.filter(item => !itemIds.includes(item.id));

                    return {
                        ...opt,
                        items: newItems
                    };
                }
                return opt;
            })
        }));
    };

    const handleOptionChange = (optionId, field, value) => {
        setFormData(prevData => ({
            ...prevData,
            options: prevData.options.map(opt =>
                opt.id === optionId ? { ...opt, [field]: value } : opt
            )
        }));
    };

    const MenuCompositionSection = () => {
        return (
            <Box sx={{ mt: 3 }}>
                <SectionTitle>Composition du Menu</SectionTitle>

                {/* Boutons d'ajout d'options */}
                <Box sx={{
                    display: 'flex',
                    gap: 1,
                    flexWrap: 'wrap',
                    mb: 3
                }}>
                    {MENU_ITEM_TYPES.map(type => {
                        const isUsed = formData.options.some(opt => opt.type === type.id);
                        return (
                            <Button
                                key={type.id}
                                variant="outlined"
                                startIcon={<FaPlus />}
                                onClick={() => handleAddOption(type.id)}
                                disabled={isUsed}
                                sx={{
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    opacity: isUsed ? 0.5 : 1
                                }}
                            >
                                {type.name}
                            </Button>
                        );
                    })}
                </Box>

                {/* Liste des options */}
                {MENU_ITEM_TYPES.map(type => {
                    const option = formData.options.find(opt => opt.type === type.id);
                    if (!option) return null;

                    return (
                        <OptionSection
                            key={option.id}
                            option={option}
                            typeInfo={type}
                            menuItems={menuItems}
                            categories={categories}
                            onOptionChange={handleOptionChange}
                            onRemoveOption={handleRemoveOption}
                            onItemSelection={handleItemSelection}
                            onMultipleItemSelection={handleMultipleSelection}
                            onExtraPriceChange={handleItemExtraPriceChange}
                        />
                    );
                })}
            </Box>
        );
    };


    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            width: '100%',
            position: 'relative',
            bgcolor: 'background.default',
        }}>
            <Box sx={{
                flexGrow: 1,
                overflowY: 'auto',
                p: 3,
                pb: 10
            }}>
                <Box>
                    {/* En-tête */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h4" fontWeight="bold" color="primary.main">
                            {bundle?.id ? 'Modifier la formule' : 'Nouvelle formule'}
                        </Typography>
                        <IconButton onClick={onClose} sx={{ color: 'text.secondary' }}>
                            <FaTimes />
                        </IconButton>
                    </Box>

                    {/* Informations générales */}
                    <SectionTitle>Informations générales</SectionTitle>
                    <TextField
                        fullWidth
                        label="Nom de la formule"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        margin="normal"
                        required
                    />

                    <TextField
                        fullWidth
                        label="Description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        margin="normal"
                        multiline
                        rows={3}
                    />

                    <FormControlLabel
                        control={
                            <Switch
                                checked={formData.available}
                                onChange={(e) => handleChange({
                                    target: {
                                        name: 'available',
                                        type: 'checkbox',
                                        checked: e.target.checked
                                    }
                                })}
                            />
                        }
                        label="Disponible"
                    />

                    {/* Section Prix */}
                    <PricingSection
                        formData={formData}
                        setFormData={setFormData}
                    />
                    {/* Section Validité */}
                    {/* <ValiditySection /> */}

                    {/* Section Composition du Menu */}
                    <MenuCompositionSection />
                </Box>
            </Box>

            {/* Pied de page avec les boutons d'action */}
            <Box sx={{
                position: 'sticky',
                bottom: 0,
                left: 0,
                right: 0,
                backgroundColor: 'background.paper',
                boxShadow: '0px -2px 4px rgba(0,0,0,0.1)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2,
                zIndex: 1
            }}>
                <Button
                    variant="outlined"
                    onClick={onClose}
                    disabled={isSaving}
                    startIcon={<FaTimes />}
                >
                    Annuler
                </Button>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {formData.options.length === 0 && (
                        <Typography variant="body2" color="error">
                            Ajoutez au moins une option au menu
                        </Typography>
                    )}
                    {isSaving && <CircularProgress size={24} />}
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        disabled={isSaving || formData.options.length === 0}
                        startIcon={<FaSave />}
                    >
                        {isSaving ? 'Enregistrement...' : 'Enregistrer'}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}

export default BundleEditPanel;